<script setup>
import { ref, onMounted } from 'vue'
import PanelOrganic from '@/design/primitives/PanelOrganic.vue'
import ButtonSeed from '@/design/primitives/ButtonSeed.vue'
import { api } from '@/api/client.js'
import { EP } from '@/api/endpoints.js'

const deliveries = ref([])
const loading    = ref(false)
const error      = ref(null)
let   fetched    = false

async function load() {
  if (fetched) return
  loading.value = true
  error.value   = null
  try {
    const res = await api.get(EP.userDeliveries)
    deliveries.value = Array.isArray(res.data) ? res.data : []
    fetched = true
  } catch (e) {
    error.value = e.message || 'Could not load deliveries.'
  } finally {
    loading.value = false
  }
}

function formatDate(raw) {
  if (!raw) return '—'
  return new Date(raw).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  })
}

function statusClass(status) {
  const s = (status || 'pending').toLowerCase()
  return `badge--${s}`
}

onMounted(load)
</script>

<template>
  <section class="dossier-section" aria-labelledby="deliveries-heading">
    <h2 id="deliveries-heading" class="stamp u-display u-allcaps">Deliveries</h2>

    <PanelOrganic tone="strong" :padded="true">
      <!-- Loading -->
      <div v-if="loading" class="ledger-state" aria-live="polite">
        <span class="dossier-spinner" aria-hidden="true" />
        <span class="ledger-state__text u-display u-allcaps">Loading deliveries…</span>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="ledger-state ledger-state--err" aria-live="assertive">
        <span class="ledger-state__text">{{ error }}</span>
        <ButtonSeed variant="ghost" size="sm" @click="() => { fetched = false; load() }">RETRY</ButtonSeed>
      </div>

      <!-- Empty -->
      <div v-else-if="!deliveries.length" class="ledger-state ledger-state--empty">
        <span class="u-display u-allcaps">No deliveries on record</span>
      </div>

      <!-- Ledger -->
      <ul v-else class="ledger" role="list" aria-label="Deliveries">
        <li class="ledger__header" aria-hidden="true">
          <span class="ledger__col ledger__col--id      u-display u-allcaps">Order</span>
          <span class="ledger__col ledger__col--product u-display u-allcaps">Product</span>
          <span class="ledger__col ledger__col--qty     u-display u-allcaps">Qty</span>
          <span class="ledger__col ledger__col--status  u-display u-allcaps">Status</span>
          <span class="ledger__col ledger__col--ts      u-display u-allcaps">Timestamp</span>
        </li>

        <li
          v-for="d in deliveries"
          :key="d.orderId"
          class="ledger__row"
        >
          <span class="ledger__col ledger__col--id u-mono ledger__id">{{ d.orderId ?? '—' }}</span>
          <span class="ledger__col ledger__col--product u-display u-allcaps ledger__product">
            {{ d.productName ?? '—' }}
          </span>
          <span class="ledger__col ledger__col--qty u-mono">{{ d.quantity ?? '—' }}</span>
          <span
            class="ledger__col ledger__col--status ledger__badge"
            :class="statusClass(d.deliveryStatus)"
          >
            {{ d.deliveryStatus || 'pending' }}
          </span>
          <span class="ledger__col ledger__col--ts u-mono">
            {{ formatDate(d.deliveredAt ?? d.createdAt) }}
          </span>

          <!-- Delivery error inline -->
          <div v-if="d.deliveryError" class="ledger__err-row">
            <span class="ledger__err">{{ d.deliveryError }}</span>
          </div>
        </li>
      </ul>
    </PanelOrganic>
  </section>
</template>

<style scoped>
.dossier-section {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.stamp {
  font-size: clamp(var(--fs-21), 2.5vw, var(--fs-32));
  color: var(--gold);
  letter-spacing: 0.12em;
  margin: 0;
}

/* Ledger */
.ledger {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.ledger__header,
.ledger__row {
  display: grid;
  grid-template-columns: 130px 1fr 60px 110px 160px;
  align-items: center;
  gap: var(--sp-3) var(--sp-4);
  padding: var(--sp-3) var(--sp-4);
}

.ledger__header {
  border-bottom: 1px solid var(--ash);
  margin-bottom: var(--sp-1);
}

.ledger__header .ledger__col {
  font-size: var(--fs-11, 0.6875rem);
  color: var(--ink-mute);
  letter-spacing: 0.14em;
}

.ledger__row {
  border-bottom: 1px solid var(--ash);
  transition: background var(--t-fast) var(--ease-cinema);
  /* rows with errors expand to two grid rows */
  flex-wrap: wrap;
}
.ledger__row:last-child { border-bottom: none; }
.ledger__row:hover { background: rgba(229, 199, 107, 0.04); }

.ledger__col { min-width: 0; font-size: var(--fs-14); color: var(--ink-soft); }

.ledger__id {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--fs-12);
  color: var(--ink-mute);
}

.ledger__product {
  font-size: var(--fs-13, 0.8125rem);
  color: var(--ink);
  letter-spacing: 0.06em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ledger__err-row {
  grid-column: 1 / -1;
  padding: 2px 0 var(--sp-2);
}

.ledger__err {
  font-size: var(--fs-12);
  color: var(--ember);
  font-family: var(--font-mono);
}

.ledger__badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: var(--r-xs);
  font-family: var(--font-display);
  font-size: var(--fs-11, 0.6875rem);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  white-space: nowrap;
  border: 1px solid var(--ash);
  background: var(--bg-deep);
  color: var(--ink-mute);
}
.badge--sent,
.badge--delivered  { background: var(--gold-soft); color: var(--gold); border-color: rgba(229, 199, 107, 0.3); }
.badge--pending    { background: rgba(184, 90, 58, 0.12); color: var(--copper); border-color: rgba(184, 90, 58, 0.3); }
.badge--failed     { background: rgba(230, 76, 43, 0.12); color: var(--ember); border-color: rgba(230, 76, 43, 0.3); }

/* States */
.ledger-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-3);
  padding: var(--sp-12) var(--sp-4);
  color: var(--ink-soft);
}
.ledger-state--err   { color: var(--ember); flex-direction: column; }
.ledger-state--empty { color: var(--ink-mute); }
.ledger-state__text  { font-size: var(--fs-14); }

.dossier-spinner {
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 2px solid var(--ash);
  border-top-color: var(--gold);
  border-radius: 50%;
  animation: spin 1.2s linear infinite;
  flex-shrink: 0;
}
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 760px) {
  .ledger__header,
  .ledger__row {
    grid-template-columns: 90px 1fr 90px 130px;
  }
  .ledger__col--qty { display: none; }
}

@media (max-width: 540px) {
  .ledger__header,
  .ledger__row {
    grid-template-columns: 1fr 90px 110px;
  }
  .ledger__col--id { display: none; }
  .ledger__col--ts { display: none; }
}
</style>
